/**
 * Brazil contact adapter (S011): CPF/CNPJ/CEP validators, +55 phone, and BR address rules — the
 * document validators S009 deliberately deferred (ADR-0013 boundary). These are Brazil rules and live in
 * the Brazil pack; the universal `@vello/contact` contracts know only country-scoped `type` strings and an
 * adapter port. The neutrality gate keeps CPF/CNPJ/CEP/`+55` out of universal code.
 */
import { ok, err } from '@vello/core';
import { BRAZIL, BRAZIL_CALLING_CODE } from './brazil.js';
const digits = (v) => v.replace(/\D/g, '');
const allSame = (s) => /^(\d)\1*$/.test(s);
// Accepted input shapes: bare digits or the canonical mask. Anything else (e.g. `abc111...xyz`) is rejected
// before normalization, so junk-wrapped values never validate.
const CPF_INPUT = /^\d{11}$|^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
const CNPJ_INPUT = /^\d{14}$|^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;
const CEP_INPUT = /^\d{8}$|^\d{5}-\d{3}$/;
const PHONE_INPUT = /^\+?[\d\s()-]+$/; // digits and phone punctuation only — never letters
// ---- CPF / CNPJ (mod-11 check digits) --------------------------------------------------------------
function checkDigit(base, startWeight) {
    let sum = 0;
    let weight = startWeight;
    for (const ch of base) {
        sum += Number(ch) * weight;
        weight -= 1;
    }
    const r = (sum * 10) % 11;
    return r === 10 ? 0 : r;
}
/** Brazilian individual taxpayer id (CPF), 11 digits with two mod-11 check digits. */
export function isValidCpf(value) {
    if (!CPF_INPUT.test(value.trim()))
        return false;
    const d = digits(value);
    if (d.length !== 11 || allSame(d))
        return false;
    const d1 = checkDigit(d.slice(0, 9), 10);
    const d2 = checkDigit(d.slice(0, 10), 11);
    return d1 === Number(d[9]) && d2 === Number(d[10]);
}
function cnpjDigit(base) {
    // weights run 2..9 cycling from the right
    let sum = 0;
    let weight = 2;
    for (let i = base.length - 1; i >= 0; i--) {
        sum += Number(base[i]) * weight;
        weight = weight === 9 ? 2 : weight + 1;
    }
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
}
/** Brazilian company id (CNPJ), 14 digits with two mod-11 check digits. */
export function isValidCnpj(value) {
    if (!CNPJ_INPUT.test(value.trim()))
        return false;
    const d = digits(value);
    if (d.length !== 14 || allSame(d))
        return false;
    const d1 = cnpjDigit(d.slice(0, 12));
    const d2 = cnpjDigit(d.slice(0, 13));
    return d1 === Number(d[12]) && d2 === Number(d[13]);
}
/** Brazilian postal code (CEP), 8 digits and not all-equal. */
export function isValidCep(value) {
    if (!CEP_INPUT.test(value.trim()))
        return false;
    const d = digits(value);
    return d.length === 8 && !allSame(d);
}
// ---- Display formatters (S011 owns document/phone/address presentation) ----------------------------
export function formatCpf(value) {
    const d = digits(value);
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
}
export function formatCnpj(value) {
    const d = digits(value);
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
}
export function formatCep(value) {
    const d = digits(value);
    return `${d.slice(0, 5)}-${d.slice(5, 8)}`;
}
// ---- Phone (national ↔ E.164 for calling code 55) --------------------------------------------------
/** National Brazil number: DDD (2, 11–99) + subscriber (8 landline / 9 mobile, mobile starts with 9). */
function isValidBrNational(national) {
    if (!/^\d{10,11}$/.test(national))
        return false;
    const ddd = Number(national.slice(0, 2));
    if (ddd < 11 || ddd > 99)
        return false;
    if (national.length === 11 && national[2] !== '9')
        return false;
    return true;
}
/** Normalize a national or E.164 Brazil phone into an E.164 `PhoneNumber` (`+55…`). */
export function brazilPhone(input) {
    if (!PHONE_INPUT.test(input.trim()))
        return err('INVALID_BR_PHONE');
    let d = digits(input);
    if (d.startsWith(BRAZIL_CALLING_CODE) && (d.length === 12 || d.length === 13)) {
        d = d.slice(BRAZIL_CALLING_CODE.length);
    }
    if (!isValidBrNational(d))
        return err('INVALID_BR_PHONE');
    return ok({ e164: `+${BRAZIL_CALLING_CODE}${d}` });
}
/** Masked national display, e.g. `(38) 99123-4567`. Accepts national or E.164 input. */
export function formatBrazilPhone(input) {
    let d = digits(input);
    if (d.startsWith(BRAZIL_CALLING_CODE) && (d.length === 12 || d.length === 13)) {
        d = d.slice(BRAZIL_CALLING_CODE.length);
    }
    const ddd = d.slice(0, 2);
    const rest = d.slice(2);
    const mid = rest.length === 9 ? rest.slice(0, 5) : rest.slice(0, 4);
    const end = rest.length === 9 ? rest.slice(5) : rest.slice(4);
    return `(${ddd}) ${mid}-${end}`;
}
/**
 * Progressive writing of a Brazilian national number being typed (no parentheses - the field already shows `+55`):
 * `34`, `34 9`, `34 90000`, `34 90000-1`, `34 90000-1001` (mobile, 11 digits); `34 3000-1001` (landline, 10). The
 * subscriber is grouped 5-4 when it starts with the mobile 9, 4-4 otherwise, so the hyphen never jumps while typing.
 */
function typeBrazilPhone(partial) {
    const d = partial.replace(/\D/g, '').slice(0, 11);
    const ddd = d.slice(0, 2);
    const rest = d.slice(2);
    if (rest === '')
        return ddd;
    const split = rest.startsWith('9') ? 5 : 4;
    const subscriber = rest.length > split ? `${rest.slice(0, split)}-${rest.slice(split)}` : rest;
    return `${ddd} ${subscriber}`;
}
/**
 * How Brazil writes a phone (D580): the DDD in parentheses and a 4-4 landline / 5-4 mobile subscriber nationally
 * (`(34) 90000-1001`); the same groups without parentheses after `+55` internationally (`+55 34 90000-1001`).
 * A number that is not a 10- or 11-digit DDD + subscriber is left to the universal `+CC rest` fallback.
 */
export const BRAZIL_PHONE_DISPLAY = {
    countryCode: BRAZIL,
    callingCode: BRAZIL_CALLING_CODE,
    national: (d) => (/^\d{10,11}$/.test(d) ? formatBrazilPhone(d) : undefined),
    international: (d) => (/^\d{10,11}$/.test(d) ? formatBrazilPhone(d).replace(/^\((\d{2})\) /, '$1 ') : undefined),
    maxNationalDigits: 11,
    typing: typeBrazilPhone,
    // D717: the rider's own number in Conta / Perfil keeps the DDD and the first digit, and the last four - `(34) 9****-1001`.
    maskKeep: { leading: 3, trailing: 4 },
};
// ---- Address (BR requires a valid CEP, a locality, and a 2-letter UF) ------------------------------
export function brazilAddress(address) {
    if (address.postalCode === undefined || !isValidCep(address.postalCode))
        return err('INVALID_CEP');
    if (address.locality === undefined || address.locality.trim() === '')
        return err('MISSING_LOCALITY');
    if (address.region === undefined || !/^[A-Z]{2}$/.test(address.region))
        return err('INVALID_UF');
    return ok({ ...address, postalCode: formatCep(address.postalCode) });
}
// ---- The Brazil contact adapter --------------------------------------------------------------------
const cpfDoc = (v) => (isValidCpf(v) ? ok(digits(v)) : err('INVALID_CPF'));
const cnpjDoc = (v) => isValidCnpj(v) ? ok(digits(v)) : err('INVALID_CNPJ');
/** BR adapter for the universal `@vello/contact` registry. Registered by the Brazil pack. */
export const BRAZIL_CONTACT_ADAPTER = {
    countryCode: BRAZIL,
    documents: { CPF: cpfDoc, CNPJ: cnpjDoc },
    phone: brazilPhone,
    address: brazilAddress,
};
/** Convenience: a `CPF` identity document for Brazil. */
export function brazilCpfDocument(value) {
    return { countryCode: BRAZIL, type: 'CPF', value };
}
//# sourceMappingURL=contact.js.map